import { useEffect, useRef, useCallback, useMemo } from "react";
import { useTheme } from "@mui/material";
import mapboxgl, { GeoJSONSource, MapMouseEvent } from "mapbox-gl";
import { GeospatialProject } from "./useGeospatialProjectsData";

const SOURCE_ID = "projects-source";
const CLUSTERS_LAYER = "clusters";
const CLUSTER_COUNT_LAYER = "cluster-count";
const UNCLUSTERED_POINT_LAYER = "unclustered-point";
const CLUSTER_ZOOM = 14;

interface UseProjectClustersLayerOptions {
  map: mapboxgl.Map | null;
  projects: GeospatialProject[];
  onProjectSelect?: (project: GeospatialProject | null) => void;
}

export const useProjectClustersLayer = ({
  map,
  projects,
  onProjectSelect,
}: UseProjectClustersLayerOptions) => {
  const theme = useTheme();
  const eventHandlersRef = useRef<{
    onClusterClick: ((e: MapMouseEvent) => void) | null;
    onPointClick: ((e: MapMouseEvent) => void) | null;
  }>({
    onClusterClick: null,
    onPointClick: null,
  });

  const geoJsonData = useMemo(() => {
    const features = projects
      .filter((p) => p.longitude && p.latitude)
      .map((project) => {
        const ragStatus = project.rag?.overall || "green";
        return {
          type: "Feature" as const,
          geometry: {
            type: "Point" as const,
            coordinates: [project.longitude!, project.latitude!],
          },
          properties: {
            id: project.id,
            title: project.title,
            project_reference: project.project_reference,
            address: project.address,
            programme: project.programme,
            network: project.network,
            site: project.site,
            phase_name: project.phase_name,
            overall_rag: ragStatus,
            progress: project.progress?.percentage || 0,
            is_red: ragStatus === "red" ? 1 : 0,
            is_amber: ragStatus === "amber" ? 1 : 0,
          },
        };
      });

    return {
      type: "FeatureCollection" as const,
      features,
    };
  }, [projects]);

  const detachEventHandlers = useCallback((mapInstance: mapboxgl.Map) => {
    const handlers = eventHandlersRef.current;
    if (handlers.onClusterClick) {
      mapInstance.off("click", CLUSTERS_LAYER, handlers.onClusterClick);
    }
    if (handlers.onPointClick) {
      mapInstance.off("click", UNCLUSTERED_POINT_LAYER, handlers.onPointClick);
    }
    eventHandlersRef.current = {
      onClusterClick: null,
      onPointClick: null,
    };
  }, []);

  const attachEventHandlers = useCallback(
    (mapInstance: mapboxgl.Map, projectsList: GeospatialProject[]) => {
      detachEventHandlers(mapInstance);

      const onClusterClick = (e: MapMouseEvent) => {
        const features = mapInstance.queryRenderedFeatures(e.point, {
          layers: [CLUSTERS_LAYER],
        });

        if (!features.length) return;

        const clusterId = features[0].properties?.cluster_id;
        const source = mapInstance.getSource(SOURCE_ID) as GeoJSONSource;

        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;

          const geometry = features[0].geometry;
          if (geometry.type === "Point") {
            mapInstance.easeTo({
              center: geometry.coordinates as [number, number],
              zoom: zoom ?? CLUSTER_ZOOM,
            });
          }
        });
      };

      const onPointClick = (e: MapMouseEvent) => {
        const features = mapInstance.queryRenderedFeatures(e.point, {
          layers: [UNCLUSTERED_POINT_LAYER],
        });

        if (!features.length) return;

        const projectId = features[0].properties?.id;
        const project = projectsList.find((p) => p.id === projectId);

        if (project && onProjectSelect) {
          onProjectSelect(project);

          const geometry = features[0].geometry;
          if (geometry.type === "Point") {
            mapInstance.easeTo({
              center: geometry.coordinates as [number, number],
              zoom: Math.max(mapInstance.getZoom(), 12),
            });
          }
        }
      };

      eventHandlersRef.current = {
        onClusterClick,
        onPointClick,
      };

      mapInstance.on("click", CLUSTERS_LAYER, onClusterClick);
      mapInstance.on("click", UNCLUSTERED_POINT_LAYER, onPointClick);

      mapInstance.on("mouseenter", CLUSTERS_LAYER, () => {
        mapInstance.getCanvas().style.cursor = "pointer";
      });
      mapInstance.on("mouseleave", CLUSTERS_LAYER, () => {
        mapInstance.getCanvas().style.cursor = "";
      });
      mapInstance.on("mouseenter", UNCLUSTERED_POINT_LAYER, () => {
        mapInstance.getCanvas().style.cursor = "pointer";
      });
      mapInstance.on("mouseleave", UNCLUSTERED_POINT_LAYER, () => {
        mapInstance.getCanvas().style.cursor = "";
      });
    },
    [detachEventHandlers, onProjectSelect]
  );

  const initializeLayers = useCallback(
    (mapInstance: mapboxgl.Map) => {
      try {
        if (mapInstance.getLayer(UNCLUSTERED_POINT_LAYER)) {
          mapInstance.removeLayer(UNCLUSTERED_POINT_LAYER);
        }
        if (mapInstance.getLayer(CLUSTER_COUNT_LAYER)) {
          mapInstance.removeLayer(CLUSTER_COUNT_LAYER);
        }
        if (mapInstance.getLayer(CLUSTERS_LAYER)) {
          mapInstance.removeLayer(CLUSTERS_LAYER);
        }
        if (mapInstance.getSource(SOURCE_ID)) {
          mapInstance.removeSource(SOURCE_ID);
        }

        mapInstance.addSource(SOURCE_ID, {
          type: "geojson",
          data: geoJsonData,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
          clusterProperties: {
            red_count: ["+", ["get", "is_red"]],
            amber_count: ["+", ["get", "is_amber"]],
          },
        });

        mapInstance.addLayer({
          id: CLUSTERS_LAYER,
          type: "circle",
          source: SOURCE_ID,
          filter: ["has", "point_count"],
          paint: {
            "circle-color": [
              "case",
              [">", ["get", "red_count"], 0],
              theme.palette.error.main,
              [">", ["get", "amber_count"], 0],
              theme.palette.warning.main,
              theme.palette.success.main,
            ],
            "circle-radius": [
              "step",
              ["get", "point_count"],
              20,
              10,
              25,
              50,
              30,
              100,
              35,
            ],
            "circle-stroke-width": 2,
            "circle-stroke-color": theme.palette.background.paper,
          },
        });

        mapInstance.addLayer({
          id: CLUSTER_COUNT_LAYER,
          type: "symbol",
          source: SOURCE_ID,
          filter: ["has", "point_count"],
          layout: {
            "text-field": ["get", "point_count_abbreviated"],
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12,
          },
          paint: {
            "text-color": theme.palette.common.white,
          },
        });

        mapInstance.addLayer({
          id: UNCLUSTERED_POINT_LAYER,
          type: "circle",
          source: SOURCE_ID,
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-color": [
              "match",
              ["get", "overall_rag"],
              "red",
              theme.palette.error.main,
              "amber",
              theme.palette.warning.main,
              "green",
              theme.palette.success.main,
              theme.palette.success.main,
            ],
            "circle-radius": 10,
            "circle-stroke-width": 2,
            "circle-stroke-color": theme.palette.background.paper,
          },
        });
      } catch (error) {
        console.error("Error initializing cluster layers:", error);
      }
    },
    [geoJsonData, theme]
  );

  useEffect(() => {
    if (!map || projects.length === 0) return;

    const initializeLayersAndHandlers = () => {
      initializeLayers(map);
      attachEventHandlers(map, projects);
    };

    if (map.loaded()) {
      initializeLayersAndHandlers();
    }

    map.on("style.load", initializeLayersAndHandlers);

    return () => {
      map.off("style.load", initializeLayersAndHandlers);
      detachEventHandlers(map);
    };
  }, [map, projects, initializeLayers, attachEventHandlers, detachEventHandlers]);
};

